import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { type EntityTone, entityStyle, entityTone } from '../lib/entity-color';

/**
 * Etykieta encji (lifting 17.10.2026, DECISIONS „Zadania łatwiejsze w użyciu”, DESIGN „Lifting 17.10.2026”): mały
 * chip z nazwą klienta albo projektu, żeby wiersze szybko się od siebie odróżniały.
 *
 *  - Klient (`ClientChip`): lekki kolor stały dla klienta (lib/entity-color.ts — własny kolor klienta albo barwa
 *    z identyfikatora), tło + tekst z tokenów `--entity-*` (AA w jasnym i ciemnym motywie).
 *  - Projekt (`ProjectChip`): neutralny chip z ramką — czytelniej niż drugi kolor obok klienta; projekt wewnętrzny
 *    (bez klienta) wygląda tak samo.
 *
 * Sama etykieta nie jest linkiem (wiersze list są w całości klikalne); długie nazwy skraca wielokropek
 * (pełna nazwa w podpowiedzi).
 */
export function EntityLabel({
  tone,
  children,
  title,
  className,
}: {
  /** barwa palety; `neutral` = chip z ramką bez koloru */
  tone: EntityTone | 'neutral';
  children: ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <span
      data-entity-tone={tone}
      title={title ?? (typeof children === 'string' ? children : undefined)}
      style={tone === 'neutral' ? undefined : entityStyle(tone)}
      className={cn(
        'inline-flex h-5 max-w-[180px] min-w-0 shrink items-center rounded-sm px-1.5 text-caption leading-none font-medium whitespace-nowrap',
        tone === 'neutral' && 'border border-line text-fg-secondary',
        className,
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/** Chip klienta w jego stałej barwie. */
export function ClientChip({
  client,
  className,
}: {
  client: { id: string; name: string; color?: string | null };
  className?: string;
}) {
  return (
    <EntityLabel tone={entityTone(client.id, client.color)} className={className}>
      {client.name}
    </EntityLabel>
  );
}

/** Neutralny chip projektu (albo usługi). */
export function ProjectChip({ name, className }: { name: string; className?: string }) {
  return (
    <EntityLabel tone="neutral" className={className}>
      {name}
    </EntityLabel>
  );
}
